import {
  IFoodFieldsFragment,
  IUserIngredientFieldsFragment,
  SearchSource,
  useSearchFoodQuery
} from "app/gql/graphqlSchema";
import {useCallback, useMemo, useRef, useState} from "react";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useRole
} from "@floating-ui/react";
import Search from "antd/es/input/Search";
import {isNil} from "app/util/stdlib";
import {Button, InputRef} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import {useVirtualizer} from "@tanstack/react-virtual";
import {FoodItem} from "app/food/FoodItem";
import {useImmer} from "use-immer";

interface IProps {
  onSelect?: (food: IFoodFieldsFragment | IUserIngredientFieldsFragment) => void;
  onCreateNew?: (term: string) => void | Promise<void>;
  className?: string;
}

export const SearchFood = ({onSelect, onCreateNew, className}: IProps) => {
  const [term, setTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [searchSources] = useImmer<SearchSource[]>([SearchSource.Mine, SearchSource.CofId, SearchSource.Cnf]);

  const {data, previousData, loading} = useSearchFoodQuery({
    variables: {
      input: {
        term: term.trim(),
        searchSources: searchSources.length > 0 ? searchSources : null
      }
    },
    skip: term.trim() === ""
  });

  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const {refs, floatingStyles, context} = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      flip(),
      offset(4),
      size({
        apply({elements, availableHeight, rects}) {
          elements.floating.style.maxHeight = `${availableHeight}px`;
          elements.floating.style.width = `${rects.reference.width}px`;
        }
      })
    ],
    placement: "bottom-start",
    whileElementsMounted: autoUpdate
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, {role: "listbox"});

  const {getReferenceProps, getFloatingProps, getItemProps} = useInteractions([dismiss, role]);

  const items = useMemo(() => {
    const foods = data?.searchFood.foods ?? previousData?.searchFood.foods ?? [];
    const userIngredients = data?.searchFood.userIngredients ?? previousData?.searchFood.userIngredients ?? [];

    return [...userIngredients, ...foods];
  }, [
    data?.searchFood.foods,
    data?.searchFood.userIngredients,
    previousData?.searchFood.foods,
    previousData?.searchFood.userIngredients
  ]);

  const searchRef = useRef<InputRef>(null);

  // The scrollable element for your list
  const parentRef = useRef(null);

  // The virtualizer
  const getScrollElement = useCallback(() => parentRef.current, []);
  const estimateSize = useCallback(() => 45, []);
  const getItemKey = useCallback(
    (index: number) => {
      const item = items[index];
      return `${item.__typename}-${item.id}`;
    },
    [items]
  );

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement,
    estimateSize,
    getItemKey
  });

  return (
    <div className={className}>
      <div ref={refs.setReference} {...getReferenceProps()} role="combobox">
        <Search
          enterButton="Search"
          loading={loading}
          onChange={(event) => {
            setTerm(event.target.value);
            setIsOpen(true);
          }}
          onKeyDown={(event) => {
            let increment = null;
            if (event.key === "ArrowDown") {
              increment = 1;
              event.preventDefault();
            } else if (event.key === "ArrowUp") {
              increment = -1;
              event.preventDefault();
            } else if (event.key === "PageDown") {
              increment = 10;
              event.preventDefault();
            } else if (event.key === "PageUp") {
              increment = -10;
              event.preventDefault();
            }
            if (increment !== null) {
              let clampedIndex = activeIndex;
              if (clampedIndex === null || clampedIndex < 0 || clampedIndex >= items.length) {
                clampedIndex = null;
              }

              const newIndex = Math.max(0, Math.min(items.length - 1, (clampedIndex ?? -1) + increment));
              setActiveIndex(newIndex);
              rowVirtualizer.scrollToIndex(newIndex);
            }
          }}
          onSearch={() => {
            if (!isNil(activeIndex) && activeIndex >= 0 && activeIndex < items.length) {
              onSelect?.(items[activeIndex]);
              setTerm("");
              setActiveIndex(null);
            } else {
              setIsOpen(true);
              searchRef.current?.focus();
            }
          }}
          placeholder="Search for ingredient"
          ref={searchRef}
          value={term}
        />
      </div>
      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} initialFocus={-1}>
            <div
              className="shadow-lg rounded-md bg-white dark:bg-gray-800 dark:text-gray-400 flex flex-col border-1 border-gray-300 dark:border-gray-700"
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              {items.length === 0 && term === "" && (
                <div className="p-2 text-gray-400 text-center">Start typing an ingredient...</div>
              )}
              {items.length === 0 && term !== "" && (
                <div className="p-2 text-gray-700 text-center">
                  Ingredient <strong className="font-bold text-gray-900">{term}</strong> is not found{" "}
                  <Button
                    color="green"
                    icon={<PlusOutlined />}
                    onClick={async () => {
                      await onCreateNew?.(term);
                    }}
                    type="text"
                    variant="filled"
                  >
                    Create
                  </Button>
                </div>
              )}
              <div
                ref={parentRef}
                style={{
                  height: "100%",
                  overflow: "auto" // Make it scroll!
                }}
              >
                {/* The large inner element to hold all of the items */}
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: "100%",
                    position: "relative"
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                    const item = items[virtualItem.index];
                    return (
                      <div
                        key={virtualItem.key}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: `${virtualItem.size}px`,
                          transform: `translateY(${virtualItem.start}px)`
                        }}
                      >
                        <FoodItem
                          activeIndex={activeIndex}
                          data-index={virtualItem.index}
                          food={item}
                          index={virtualItem.index}
                          onSelectFood={(food) => {
                            onSelect?.(food);
                            setTerm("");
                            setIsOpen(false);
                          }}
                          ref={rowVirtualizer.measureElement}
                          setActiveIndex={setActiveIndex}
                          {...getItemProps()}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </div>
  );
};
